import React, { memo, useCallback, useState } from 'react';
import { Handle, Position, useStore } from '@xyflow/react';
import classes from 'js/components/pipelines/FlowNodes/FlowNodes.module.scss';
import { ApiServer } from 'js/components/pipelines/cards/apiServer/ApiServer/ApiServer';
import { SuccessModifier } from 'js/components/pipelines/cards/apiServer/SuccessModifier/SuccessModifier';
import { SplitterOutput } from 'js/components/pipelines/cards/apiServer/SplitterOutput/SplitterOutput';
import { Error } from 'js/components/pipelines/cards/apiServer/Error/Error';
import { Request } from 'js/components/pipelines/cards/apiServer/Request/Request';
import { Target } from 'js/components/pipelines/cards/apiServer/Target/Target';
import { Result } from 'js/components/pipelines/cards/apiServer/Result/Result';
import { ReactComponent as LeftHandle } from 'new/components/icons/handleLeft.svg';
import { StandardHandle } from './handles/StandardHandle';
import { TerminatingHandle } from './handles/TerminatingHandle';
import { useNodeCreationActions } from '../hooks/useNodeCreationActions';
import DataSetSplitDialog from 'new/components/SplitDataSet/DataSetSplitDialog';

const NODE_TYPES = {
  API_SERVER: 'api_server',
  REQUEST: 'api_request',
  SUCCESS_MODIFIER: 'api_success_modifier',
  SPLITTER_OUTPUT: 'splitter_output',
  TARGET: 'api_target',
  RESULT: 'api_result',
  ERROR: 'api_error',
  ERROR_MODIFIER: 'api_error_modifier',
} as const;

interface ApiServerNodeProps {
  data: any;
}

const ApiServerNodeComponents = {
  [NODE_TYPES.API_SERVER]: ApiServer,
  [NODE_TYPES.REQUEST]: Request,
  [NODE_TYPES.SUCCESS_MODIFIER]: SuccessModifier,
  [NODE_TYPES.SPLITTER_OUTPUT]: SplitterOutput,
  [NODE_TYPES.ERROR]: Error,
  [NODE_TYPES.TARGET]: Target,
  [NODE_TYPES.RESULT]: Result,
} as const;

export const ApiServerNode = memo(({ data, ...props }: ApiServerNodeProps) => {
  const { item, isPreview, layoutDirection = 'LR' } = data;
  const { handleTransform, handleTarget, getSplitParentDataSet } = useNodeCreationActions({ item });
  const [isSplitDialogOpen, setIsSplitDialogOpen] = useState(false);
  const onOpenSplitDialog = useCallback(() => setIsSplitDialogOpen(true), []);
  const onCloseSplitDialog = useCallback(() => setIsSplitDialogOpen(false), []);
  const isTB = layoutDirection === 'TB';

  if (!item) {
    return <div />;
  }

  const { node_type } = item;
  const Node = ApiServerNodeComponents[node_type];
  // Only an unconnected data-set node may gain a splitter. A splitter owns its
  // rule-managed outputs; ordinary API nodes keep one downstream path.
  const canSplit = item.type === 'DATA_SET' && node_type !== 'splitter';

  if (!Node) {
    console.warn(`Unknown node type: ${node_type}`);
    return <div />;
  }

  const hasIncomingConnection = useStore(
    useCallback(
      (state) => state.edges.some((edge) => edge.target === `node-${item.flow_node_id}`),
      [item.flow_node_id],
    ),
  );

  const hasChildren = item.children && item.children.length > 0;

  const nodeProps = {
    ...props,
    cardData: item,
    data: item,
    isPreview,
  };

  const targetPosition = isTB ? Position.Top : Position.Left;
  const targetStyle = isTB
    ? {
        position: 'absolute' as const,
        left: '50%',
        top: '-8px',
        transform: 'translateX(-50%)',
        width: '12px',
        height: '12px',
      }
    : {
        position: 'absolute' as const,
        top: '50%',
        left: '-8px',
        transform: 'translateY(-50%)',
        width: '12px',
        height: '12px',
      };

  return (
    <div>
      {node_type !== NODE_TYPES.API_SERVER && (
        <Handle
          isConnectable={false}
          className={classes.handle_custom_svg}
          type="target"
          position={targetPosition}
          id="left"
          style={targetStyle}
        >
          <LeftHandle
            style={{
              fill: hasIncomingConnection ? 'var(--color-primary-600)' : 'var(--color-primary-400)',
              width: '16px',
              height: '16px',
            }}
          />
        </Handle>
      )}
      {node_type !== NODE_TYPES.API_SERVER &&
        node_type !== NODE_TYPES.TARGET &&
        (hasChildren ? (
          <StandardHandle isConnectable={false} layoutDirection={layoutDirection} />
        ) : (
          <TerminatingHandle
            onTransform={handleTransform}
            onTarget={handleTarget}
            onSplit={canSplit ? onOpenSplitDialog : undefined}
            layoutDirection={layoutDirection}
          />
        ))}
      <Node {...nodeProps} />
      {isSplitDialogOpen && (
        <DataSetSplitDialog parentDataSet={getSplitParentDataSet()} onClose={onCloseSplitDialog} />
      )}
    </div>
  );
});
